import { useEffect, useState, useCallback, useMemo } from 'react';
import Pane from '../components/Pane';
import QuestionPane from '../components/QuestionPane';
import './QuestionnairePage.css';

const INTRO_PANES = 3;

function QuestionnairePage() {
  const [currentPane, setCurrentPane] = useState(0);
  const [email, setEmail] = useState('');
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [companySize, setCompanySize] = useState('');
  const [companySizeOptions, setCompanySizeOptions] = useState([]);
  const [scoreResult, setScoreResult] = useState(null);
  const [scoring, setScoring] = useState(false);
  const [reportStatus, setReportStatus] = useState(''); // '', 'sending', 'sent', 'error'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch('/api/questionnaire').then((res) => res.json()),
      fetch('/api/company-size-options').then((res) => res.json()),
    ])
      .then(([qData, csData]) => {
        setQuestions(qData.questions || []);
        setCompanySizeOptions(csData.options || []);
      })
      .catch(() => {
        setQuestions([]);
        setCompanySizeOptions([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const EXTRA_PANES = 2; // company-size pane + results pane
  const totalPanes = INTRO_PANES + questions.length + EXTRA_PANES;
  const companySizePaneIndex = INTRO_PANES + questions.length;
  const resultsPaneIndex = companySizePaneIndex + 1;
  const goNext = () => setCurrentPane((p) => Math.min(p + 1, totalPanes - 1));
  const goBack = () => setCurrentPane((p) => Math.max(p - 1, 0));

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Determine current question
  const questionIndex = currentPane - INTRO_PANES;
  const currentQuestion = questions[questionIndex];

  // Compute unique base question numbers for progress tracking (e.g. "3a" → "3")
  const baseNumbers = useMemo(() => {
    const seen = new Set();
    const bases = [];
    for (const q of questions) {
      const base = (q.number || '').replace(/[a-zA-Z]+$/, '');
      if (base && !seen.has(base)) {
        seen.add(base);
        bases.push(base);
      }
    }
    return bases;
  }, [questions]);

  const isQuestionPane = currentPane >= INTRO_PANES && currentPane < companySizePaneIndex;
  const progressPercent = useMemo(() => {
    if (!isQuestionPane || !currentQuestion) return 0;
    const currentBase = (currentQuestion.number || '').replace(/[a-zA-Z]+$/, '');
    const idx = baseNumbers.indexOf(currentBase);
    if (idx < 0) return 0;
    return Math.round(((idx + 1) / baseNumbers.length) * 100);
  }, [isQuestionPane, currentQuestion, baseNumbers]);

  const handleAnswer = (number, value) => {
    setAnswers((prev) => ({ ...prev, [number]: value }));
  };

  const fetchScore = useCallback(() => {
    setScoring(true);
    fetch('/api/score', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ answers, company_size: companySize }),
    })
      .then((res) => res.json())
      .then((data) => setScoreResult(data))
      .catch(() => setScoreResult(null))
      .finally(() => setScoring(false));
  }, [answers, companySize]);

  useEffect(() => {
    if (currentPane === resultsPaneIndex) {
      fetchScore();
    }
  }, [currentPane, resultsPaneIndex, fetchScore]);

  // Automatically send the report email once scoring completes
  useEffect(() => {
    if (!scoreResult || !scoreResult.tier || reportStatus) return;
    setReportStatus('sending');
    fetch('/api/send-report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        answers,
        company_size: companySize,
        score_result: scoreResult,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Send failed');
        return res.json();
      })
      .then(() => setReportStatus('sent'))
      .catch(() => setReportStatus('error'));
  }, [scoreResult, reportStatus, email, answers, companySize]);

  // Determine whether Next is disabled for the current question pane
  const questionNextDisabled =
    currentQuestion &&
    !QuestionPane.isComplete(currentQuestion, answers[currentQuestion.number]);

  if (loading) {
    return (
      <div className="page">
        <div className="container text-center">
          <p className="text-muted">Loading questionnaire…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container">
        {isQuestionPane && (
          <div className="progress-bar">
            <div className="progress-bar-track">
              <div
                className="progress-bar-fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="progress-bar-label">
              Question {baseNumbers.indexOf((currentQuestion?.number || '').replace(/[a-zA-Z]+$/, '')) + 1} of {baseNumbers.length}
            </span>
          </div>
        )}

        {currentPane === 0 && (
          <Pane isFirst onNext={goNext} nextDisabled={!isValidEmail}>
            <h2>CMMC Readiness Assessment</h2>
            <p>This tool will help any OSC acquire a high-level understanding of:</p>
            <ul className="intro-list">
              <li>How assessor-ready your CMMC compliance program is (or isn't)</li>
              <li>Typical pitfalls that result in assessment failings (and how to avoid them)</li>
              <li>Approximate timeline to CMMC readiness</li>
            </ul>
            <p>
              This exercise consists of approximately 20 questions which can be completed in
              15 to 20 minutes. A report will be generated for you upon completion.
            </p>
            <div className="form-group">
              <label className="form-label" htmlFor="email">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                className="form-input"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <p className="text-muted" style={{ marginTop: '8px' }}>
                Please provide your email address so the custom assessment can be delivered to you after completion.
              </p>
            </div>
          </Pane>
        )}

        {currentPane === 1 && (
          <Pane onNext={goNext} onBack={goBack} nextLabel="Get Started">
            <h2>Before You Begin</h2>
            <p>
              This tool is not designed to replace a gap assessment which (when
              done properly) should meticulously inspect all (320) NIST 800-171
              Objectives. Rather, this tool is designed to provide an insightful
              high-level understanding of your current state, potential hazards,
              and methods to more efficiently manage your cyber compliance
              program.
            </p>
          </Pane>
        )}

        {currentPane === 2 && (
          <Pane onNext={goNext} onBack={goBack}>
            <h2>About the Benchmarks</h2>
            <p>
              The benchmarks references throughout this tool are provided from
              Certified CMMC Assessors (CCAs) at ASCERA partner 112Cyber (both a
              CMMC RPO and C3PAO). These data points are provided based upon RPO
              work that 112Cyber has provided and what is typically encountered
              when providing guidance to OSCs who retained 112Cyber to conduct
              gap assessments and remediation services for OSCs as they built
              their security and compliance programs to NIST 800-171 and CMMC
              standards.
            </p>
          </Pane>
        )}

        {currentPane >= INTRO_PANES && currentPane < companySizePaneIndex && currentQuestion && (
          <Pane
            onNext={goNext}
            onBack={goBack}
            nextDisabled={questionNextDisabled}
            wide={!!(currentQuestion.lead_in_info_text || currentQuestion.info_text)}
          >
            <QuestionPane
              question={currentQuestion}
              answer={answers[currentQuestion.number] || {}}
              onAnswer={handleAnswer}
            />
          </Pane>
        )}

        {currentPane === companySizePaneIndex && (
          <Pane onNext={goNext} onBack={goBack} nextDisabled={!companySize}>
            <div className="question-pane">
              <span className="question-number">Company Size</span>
              <h3 className="question-text">
                How would you classify the size of your company?
              </h3>
              <fieldset className="question-options">
                <legend className="question-legend">Select your answer</legend>
                {companySizeOptions.map((opt) => {
                  const label = opt.key.charAt(0).toUpperCase() + opt.key.slice(1);
                  return (
                    <label key={opt.key} className="question-option">
                      <input
                        type="radio"
                        name="company-size"
                        value={label}
                        checked={companySize === label}
                        onChange={() => {
                          setCompanySize(label);
                          setAnswers((prev) => ({ ...prev, company_size: label }));
                        }}
                      />
                      <span>{label} ({opt.text})</span>
                    </label>
                  );
                })}
              </fieldset>
            </div>
          </Pane>
        )}

        {currentPane === resultsPaneIndex && (
          <Pane onBack={goBack} isLast>
            <div className="question-pane">
              <h2>Your CMMC Readiness Results</h2>
              {scoring && <p className="text-muted">Calculating your score…</p>}
              {!scoring && scoreResult && scoreResult.tier && (
                <>
                  <div className="results-summary">
                    <div className="result-item">
                      <span className="result-label">Estimated Weeks to Readiness</span>
                      <span className="result-value">{scoreResult.adjusted_weeks} weeks</span>
                    </div>
                    <div className="result-item">
                      <span className="result-label">Company Size Modifier</span>
                      <span className="result-value">{scoreResult.company_size} (×{scoreResult.modifier})</span>
                    </div>
                    <div className="result-item">
                      <span className="result-label">Est. SPRS Score Range</span>
                      <span className="result-value">{scoreResult.tier.est_sprs_score_range}</span>
                    </div>
                    <div className="result-item">
                      <span className="result-label">Maturity Assessment</span>
                      <span className="result-value">{scoreResult.tier.maturity_description}</span>
                    </div>
                  </div>
                  <div className="report-actions">
                    {reportStatus === 'sending' && (
                      <p className="text-muted">Sending your report…</p>
                    )}
                    {reportStatus === 'sent' && (
                      <p className="report-success">A full report with PowerPoint attachment has been sent to {email}.</p>
                    )}
                    {reportStatus === 'error' && (
                      <p className="report-error">We were unable to send your report. Please contact us.</p>
                    )}
                  </div>
                </>
              )}
              {!scoring && (!scoreResult || !scoreResult.tier) && (
                <p className="text-muted">Unable to calculate score. Please try again.</p>
              )}
            </div>
          </Pane>
        )}
      </div>
    </div>
  );
}

export default QuestionnairePage;
